import type {TourMeta} from '../../apis/api-tours.js';
import {getDiscreteBikingSpeed} from './cycletime.ts';

const CS: number[] = [
  14.271, 3.6992, 2.5922, -1.4384, 0.32105, 0.81542, -0.090261, -0.20757,
  0.010192, 0.028588, -0.00057466, -0.0021842, 0.000015176, 0.000086894,
  -0.00000013584, -0.0000014026,
];

export function walkingTime(length: number, heightDiff: number): number {
  /**
   * In minutes
   */
  if (length <= 0.0) {
    return 0.0;
  }

  const slope10: number = (10 * heightDiff) / length;
  let minutesPerKm: number = 0.0;

  if (slope10 > 4) {
    minutesPerKm = 17 * slope10;
  } else if (slope10 < -4) {
    minutesPerKm = -9 * slope10;
  } else {
    for (let i: number = 0; i < CS.length; i++) {
      minutesPerKm += CS[i] * Math.pow(slope10, i);
    }
  }

  const duration: number = (length * minutesPerKm) / 1000;
  return duration;
}

export function bikingTime(
  length: number,
  height: number,
  bikespeed: number,
): number {
  /**
   * In minutes
   */
  if (length <= 0) {
    return 0;
  }

  const speed: number = getDiscreteBikingSpeed(bikespeed, height / length); // in m/s
  const seconds: number = length / speed;
  return seconds / 60; // we need minutes
}

export function computeTourMeta(
  coordsXyzm: Array<[number, number, number, number]>,
  bikespeed: number,
  hikespeed: number,
  winterhikespeed: number,
  snowshoespeed: number,
  mountainbikespeed: number,
): TourMeta {
  let minEle: number = 10000.0;
  let maxEle: number = -10000.0;
  let ascent: number = 0.0;
  let descent: number = 0.0;
  let lastDist: number = -1.0;
  let lastEle: number = 0.0;
  let walkingDuration: number = 0.0;
  let bikingDuration: number = 0.0;
  let mountainBikingDuration: number = 0.0;

  for (const [, , ele, dist] of coordsXyzm) {
    minEle = Math.min(minEle, ele);
    maxEle = Math.max(maxEle, ele);

    if (lastDist !== -1.0) {
      const eleDiff: number = ele - lastEle;

      if (eleDiff > 0) {
        ascent += eleDiff;
      } else {
        descent -= eleDiff;
      }

      const length: number = dist - lastDist;
      walkingDuration += walkingTime(length, eleDiff);
      bikingDuration += bikingTime(length, eleDiff, bikespeed);
      mountainBikingDuration += bikingTime(length, eleDiff, mountainbikespeed);
    }

    lastDist = dist;
    lastEle = ele;
  }

  const meta: TourMeta = {
    length: lastDist,
    totalup: ascent,
    totaldown: descent,
    elemin: minEle,
    elemax: maxEle,
    biking: bikingDuration,
    walking: walkingDuration * hikespeed,
    winterHiking:
      Math.round(walkingDuration * 1.25 * winterhikespeed * 10) / 10,
    snowshoeTrekking:
      Math.round(walkingDuration * 1.5 * snowshoespeed * 10) / 10,
    mountainBiking: mountainBikingDuration,
  };

  return meta;
}
